import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import '../styles/Dashboard.css';
import Jaula from './Jaula';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const priorityOptions = [
  { value: 'ppa', labelKey: 'pellets_lost', fallback: 'PPA' },
  { value: 'alarms', labelKey: 'alarms', fallback: 'Alarmas' },
  { value: 'fasts', labelKey: 'fasts', fallback: 'Ayunos' },
];

const RankingJaulas = ({
  selectedDay,
  setSelectedDay,
  chartData,
  jaulaSeleccionada,
  setJaulaSeleccionada,
  viewMode,
  fastingResolution,
  titulo,
  label,
  todo,
  nivelSeleccionado,
  translate,
}) => {
  const t = (key, fallback, params) => {
    const value = typeof translate === 'function' ? translate(key, params) : key;
    return value && value !== key ? value : fallback;
  };

  const [priority, setPriority] = useState('alarms');
  const [isReversed, setIsReversed] = useState(false);
  const normalizeFastValue = (value) => (
    fastingResolution === 'day' && Number(value) === 0.5 ? 0 : (value ?? 0)
  );

  const availableDays = [
    ...new Set(
      chartData
        ?.filter((entry) => Array.isArray(entry.values_summary))
        .flatMap((entry) => entry.values_summary.map((item) => item.day)) ?? []
    ),
  ].sort((a, b) => new Date(a) - new Date(b));

  const processedJaulas = chartData.map((jaula) => {
    if (viewMode === 'week') {
      const totalAlarms = jaula?.values_summary?.reduce((sum, item) => sum + item.value.alarms_count, 0);
      const totalFasts =
        nivelSeleccionado === 'area'
          ? Math.max(...jaula?.values_summary?.map((item) => normalizeFastValue(item.value.fasts_count)))
          : jaula?.values_summary?.reduce((sum, item) => sum + normalizeFastValue(item.value.fasts_count), 0);
      const totalPPA = jaula?.values_summary?.reduce((sum, item) => sum + item.value.perdidos_count, 0);

      return {
        ...jaula,
        displayAlarms: totalAlarms,
        displayFasts: totalFasts,
        displayPPA: totalPPA,
      };
    }

    const entry = jaula?.values_summary?.find((item) => item.day === selectedDay) || {
      value: {
        alarms_count: 0,
        fasts_count: 0,
        perdidos_count: 0,
      },
    };

    return {
      ...jaula,
      displayAlarms: entry.value.alarms_count ?? 0,
      displayFasts: normalizeFastValue(entry.value.fasts_count),
      displayPPA: entry.value.perdidos_count ?? 0,
    };
  });

  processedJaulas.sort((a, b) => {
    if (priority === 'alarms') return b.displayAlarms - a.displayAlarms;
    if (priority === 'fasts') return b.displayFasts - a.displayFasts;
    if (priority === 'ppa') return b.displayPPA - a.displayPPA;
    return 0;
  });

  if (isReversed) {
    processedJaulas.reverse();
  }

  useEffect(() => {
    if (viewMode === 'day') {
      if (availableDays.length && !selectedDay) {
        setSelectedDay(availableDays[availableDays.length - 1]);
      }
    } else if (viewMode === 'week' && selectedDay) {
      setSelectedDay(null);
    }
  }, [viewMode, selectedDay, availableDays, setSelectedDay]);

  const sortedDataAlpha = useMemo(() => {
    const safeData = Array.isArray(chartData) ? chartData : [];

    return [...safeData].sort((a, b) => {
      const nameA = String(a?.name ?? '');
      const nameB = String(b?.name ?? '');
      return nameA.localeCompare(nameB);
    });
  }, [chartData]);

  return (
    <Grid
      container
      justifyContent="center"
      direction="column"
      alignItems="center"
      style={{ overflow: 'hidden', maxHeight: '88vh', minHeight: '88vh', marginTop: '-1.5vh', marginLeft: '-1vw', width: '15.4vw' }}
    >
      <Grid container justifyContent="center" alignContent="center" alignItems="center" style={{ height: '3vh' }}>
        <h2 className="h2">{titulo}</h2>
      </Grid>

     {viewMode === 'day' && (
        <Grid container justifyContent="center" alignContent="center" alignItems="center" style={{ height: '1.5vh', marginBottom: '0.5vh' }}>
          {viewMode === 'day' ? <h3 className="h3">{t('day', 'Dia')}: {selectedDay}</h3> : ''}
        </Grid>
      )}

      <Grid container justifyContent="center" alignContent="center" alignItems="center" style={{ height: '6vh', gap: '0.5vw' }}>
        <Grid container justifyContent="center" alignContent="center" alignItems="center" item xs={12} >
          <FormControl size="small" sx={{ ...textfieldStyle, position: 'relative' }}>
            <InputLabel id="select-day-label">{label}</InputLabel>
            <Select
              labelId="select-day-label"
              value={jaulaSeleccionada}
              label={label}

              onChange={(e) => {
                const selected = e.target.value;
                if (selected === todo || selected === jaulaSeleccionada) {
                  setJaulaSeleccionada('');
                } else {
                  setJaulaSeleccionada(selected);
                }
              }}
              style={{
                fontSize: '1.9vh',
                textAlign: 'center',
                marginTop: '-0.4vh',
                height: '3.5vh',
                color: 'rgba(255,255,255,0.7)',
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#252525',
                    '& .MuiMenuItem-root': {
                      fontSize: '.65vw',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#353535',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value={todo}>{todo}</MenuItem>
              {sortedDataAlpha?.map((jaula) => (
                <MenuItem
                  key={jaula.name}
                  value={jaula.name}
                  onClick={() => {
                    if (jaulaSeleccionada === jaula.name) {
                      setJaulaSeleccionada('');
                    }
                  }}
                >
                  {jaula.label ?? jaula.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container justifyContent="center" alignContent="center" alignItems="center" style={{ height: '2vh' }}>
        <ToggleButtonGroup
          value={priority}
          exclusive
          onChange={(e, val) => {
            if (val === null) {
              setIsReversed((prev) => !prev);
            } else {
              setPriority(val);
              setIsReversed(false);
            }
          }}
          size="small"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '3.5vh !important',
            maxHeight: '3.5vh !important',
            border: 0,
            p: 0,
            '& .MuiToggleButton-root': {
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.6vw',
              width: '4vw',
              backgroundColor: 'transparent !important',
              '&:hover': {
                backgroundColor: 'transparent !important',
                color: 'rgba(255,255,255,1)',
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255,255,255,1)',
                },
              },
              '&.Mui-selected': {
                backgroundColor: 'transparent !important',
                color: '#e84e0e',
                '& .MuiSvgIcon-root': {
                  color: '#e84e0e',
                },
              },
              '& .MuiTouchRipple-root': {
                display: 'none',
              },
              '& .MuiSvgIcon-root': {
                ml: 0.5,
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.7)',
                transition: 'color 0.2s',
              },
            },
          }}
        >
          {priorityOptions.map((opt, idx) => (
            <ToggleButton
              key={idx}
              value={opt.value}
              sx={{
                minWidth: '5vw',
                minHeight: '3.5vh !important',
                maxHeight: '3.5vh !important',
                fontSize: '0.6vw',
                border: 0,
                p: 0,
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                '&:hover': {
                  color: 'rgba(255,255,255,1)',
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255,255,255,1)',
                  },
                },
                '&.Mui-selected': {
                  color: 'rgba(255,255,255,0.7)',
                  '& .MuiSvgIcon-root': {
                    color: 'secondary.main',
                  },
                },
                '& .MuiSvgIcon-root': {
                  ml: 0.5,
                  fontSize: '1rem',
                  color: 'rgba(255,255,255,0.7)',
                  transition: 'color 0.2s',
                },
              }}
            >
              <Typography component="span" style={{ fontSize: '0.5vw' }}>
                {t(opt.labelKey, opt.fallback)}
              </Typography>
              <TrendingUpIcon
                style={{
                  fontSize: '1vw',
                  transform: !isReversed && opt.value === priority ? 'rotate(180deg) scaleX(-1)' : 'rotate(0deg) scaleX(1)',
                  transition: 'transform 0.3s ease-in-out',
                  color: !isReversed && opt.value === priority ? '#96191d' : '#138a3d',
                }}
              />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>

      <Grid item container justifyContent="center" alignContent="flex-start" style={{ height: viewMode === 'day' ? '70vh' : '75vh', marginTop: '1vh', width: '15vw', overflowY: 'auto', overflowX: 'hidden' }}>
        {processedJaulas.map((jaula, idx) => {
          const maxAlarmValue = Math.max(...processedJaulas.map((j) => j.displayAlarms));
          const maxFastValue = Math.max(...processedJaulas.map((j) => j.displayFasts));
          const maxPPAValue = Math.max(...processedJaulas.map((j) => j.displayPPA));

          return (
            <Grid item container justifyContent="center" key={idx} style={{ height: '15vh' }}>
              <Jaula
                {...jaula}
                viewMode={viewMode}
                maxAlarmValue={maxAlarmValue}
                maxFastValue={maxFastValue}
                maxPPAValue={maxPPAValue}
                selectedDay={selectedDay}
                priority={priority}
                fastingResolution={fastingResolution}
                nivelSeleccionado={nivelSeleccionado}
                translate={translate}
              />
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default RankingJaulas;

const textfieldStyle = {
  width: '14vw',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(233, 78, 26, .3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(233, 78, 26, .7)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(233, 78, 26, .9)',
    },
    '& .MuiInputBase-input': {
      color: '#c3c3c3',
      fontSize: '0.8vw',
    },
    '& .MuiSelect-select': {
      textAlign: 'center',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255,255,255,0.7)',
      fontSize: '1vw',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#c3c3c3',
    fontSize: '0.8vw',
    top: '50%',
    left: '35%',
    transform: 'translateY(-50%)',
    transition: 'all 0.2s ease',
  },
  '& .MuiInputLabel-shrink': {
    top: '-50%',
    left: '9%',
    transform: 'translateY(0)',
    fontSize: '0.7vw',
  },
};
